/**
 * MUI ButtonGroup component that replaces the styled-components ButtonGroup
 * 
 * This component provides a Material UI equivalent of the original ButtonGroup component
 * with similar props and functionality.
 */
import React, { useState, useEffect, cloneElement, Fragment, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonGroup as MuiButtonGroupBase, styled, Tooltip, Box, SvgIcon, useTheme } from '@mui/material';
import { findKey, isArray, isEqual, keys, pick, values } from 'lodash';
import Button from './Button';

// Create a styled nav button that overrides MUI defaults
const StyledNavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  width: '100%',
  justifyContent: 'flex-start',
  padding: '12px 16px',
  marginBottom: '4px',
  marginLeft: 0,
  marginRight: 0,
  borderRadius: 0,
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: '500',
  color: 'transparent',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: isSelected ? '#000000' : 'transparent',
  minWidth: 'unset',
  '&:hover': {
    backgroundColor: isSelected ? '#000000' : 'transparent',
  },
  '&:focus': {
    backgroundColor: isSelected ? '#000000' : 'transparent',
    boxShadow: 'none',
  },
  '&:active': {
    backgroundColor: isSelected ? '#000000' : 'transparent',
  },
  '&.MuiButton-root': {
    backgroundColor: isSelected ? '#000000' : 'transparent',
    '&:hover': {
      backgroundColor: isSelected ? '#000000' : 'transparent',
    },
    '&:focus': {
      backgroundColor: isSelected ? '#000000' : 'transparent',
    },
    '&:active': {
      backgroundColor: isSelected ? '#000000' : 'transparent',
    },
  },
}));
import InputLabel from '../atoms/InputLabel';
import MuiBulletList from '../atoms/BulletList';
import Navigation from 'globalConstants/Icons/Navigation';
import Furniture from 'globalConstants/Icons/Furniture';
import Actions from 'globalConstants/Icons/Actions';
import ReducersIndex from 'reduxModules/ducks';
import { getConditionResult, getCurrentValue2 } from 'reduxModules/selectors/containerSelectors';
import { 
  ButtonGroupProps, 
  SelectButtonsProps, 
  ToggleButtonsProps, 
  ToggleSelectProps, 
  DotButtonsProps,
  NavButtonsProps,
  FamilyButtonsProps 
} from 'types/components/molecules';
import { AnyAction } from '@reduxjs/toolkit';

/**
 * MUI ButtonGroup component
 * 
 * @param props - Component props
 * @returns MUI ButtonGroup component
 */
const ButtonGroup: React.FC<ButtonGroupProps> = (props) => {
  const {
    items = [],
    label,
    getState,
    checked,
    handleClick,
    tooltips,
    nav,
    gap = 1,
    id,
    className = '',
    children,
    bubbles,
    labels,
    bullets,
    isNav,
    ...otherProps
  } = props;
  
  const theme = useTheme();
  
  // Tooltip state
  const [toolTip, setToolTip] = useState<string>('');
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  
  // detect active camera
  const activeTab = useSelector((state: any) => state.container.activeTab) as string;
  const hasCamera = useSelector((state: any) => state.container.areas[activeTab]?.cameras?.hasCamera) as boolean;
  const hasVideoBar = useSelector((state: any) => 
    state.container.areas[activeTab]?.microphones?.model === 'videobar'
  ) as boolean;
  
  // Handle tooltip events
  const handleTooltipEnter = (evt: React.MouseEvent, item: string): void => {
    if (tooltips) {
      setToolTip(tooltips[item]);
      setTooltipOpen(true);
    }
  };
  
  const handleTooltipLeave = (): void => {
    setTooltipOpen(false);
  };
  
  const buttonClass = nav ? 'nav-button' : 'text-capitalize';
  
  // For specific themed components that need simpler structure, render directly
  if (props.theme === 'acousticGroupNav' || props.theme === 'speakerCoverageNavCustom' || props.theme === 'microphoneCoverageNav' || props.theme === 'speakerTypeNav') {
    return (
      <Box id={id} className={className}>
        {label && <InputLabel>{label}</InputLabel>}
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '4px',
            background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            borderRadius: '8px',
            padding: '4px',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark' ? 'inset 0 1px 3px rgba(0, 0, 0, 0.4)' : 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
            height: '36px',
            alignItems: 'center',
            ...(props.theme === 'speakerCoverageNavCustom' && { width: '120px' })
          }}
        >
          {items.map((item, index) => {
            const state = getState(item);
            const isChecked = checked(item);
            const displayText = props.theme === 'acousticGroupNav' 
              ? item 
              : (bubbles?.[item] || item);
            
            return (
              <Box
                key={`${item}-${index}`}
                sx={{
                  height: '28px',
                  padding: '0 12px',
                  borderRadius: '4px',
                  border: 'none',
                  background: isChecked
                    ? theme.palette.background.default
                    : 'transparent',
                  overflow: 'hidden',
                  color: isChecked ? theme.palette.text.primary : theme.palette.text.secondary,
                  textOverflow: 'ellipsis',
                  fontFamily: '"Open Sans"',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: isChecked ? 600 : 500,
                  lineHeight: '150%',
                  letterSpacing: '-0.28px',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  textTransform: 'capitalize',
                  minWidth: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  boxShadow: isChecked
                    ? theme.palette.mode === 'dark'
                      ? '0px 1px 3px rgba(0, 0, 0, 0.3)'
                      : '0px 1px 2px rgba(0, 0, 0, 0.1)'
                    : 'none',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: isChecked
                      ? theme.palette.background.default
                      : theme.palette.action.hover,
                    color: isChecked ? theme.palette.text.primary : theme.palette.text.primary,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: '2px',
                  },
                }}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => handleClick(e as any, item)}
              >
                {displayText}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  // Original complex structure for other themes
  return (
    <Box id={id} className={`d-flex ${props.theme === 'microphoneCoverageNav' || props.theme === 'microphoneTypeNav' || props.theme === 'speakerCoverageNav' || props.theme === 'speakerTypeNav' || props.theme === 'acousticGroupNav' || props.theme === 'speakerCoverageNavCustom' || props.theme === 'actionGroup' ? 'flex-column' : 'flex-wrap align-items-center'} ${props.theme === 'acousticGroupNav' || props.theme === 'microphoneTypeNav' || props.theme === 'microphoneCoverageNav' || props.theme === 'speakerTypeNav' || props.theme === 'speakerCoverageNav' ? 'gap-0' : 'gap-1'} ${className}`} sx={(props.theme === 'acousticGroupNav') ? { marginBottom: '8px' } : (props.theme === 'microphoneTypeNav' || props.theme === 'microphoneCoverageNav' || props.theme === 'speakerTypeNav' || props.theme === 'speakerCoverageNav') ? { marginBottom: '2px' } : {}}>
      {label && <InputLabel>{label}</InputLabel>}
      <Box className={`d-flex ${nav ? 'flex-column' : (props.theme === 'microphoneTypeNav' || props.theme === 'speakerCoverageNav' ? 'flex-row' : (props.theme === 'acousticGroupNav' || props.theme === 'microphoneCoverageNav' || props.theme === 'speakerTypeNav' || props.theme === 'speakerCoverageNavCustom' || props.theme === 'actionGroup' ? 'flex-row' : (props.theme === 'furnitureGroup' || props.theme === 'furnitureGroupNav') ? 'flex-wrap' : ''))} ${props.theme === 'microphoneTypeNav' || props.theme === 'speakerCoverageNav' ? '' : (props.theme === 'acousticGroupNav' || props.theme === 'microphoneCoverageNav' || props.theme === 'speakerTypeNav' || props.theme === 'speakerCoverageNavCustom' ? '' : 'flex-wrap')} gap-${props.theme === 'microphoneTypeNav' || props.theme === 'speakerCoverageNav' ? 0 : (props.theme === 'acousticGroupNav' || props.theme === 'microphoneCoverageNav' || props.theme === 'speakerTypeNav' || props.theme === 'speakerCoverageNavCustom' ? 1 : gap)}`} sx={(props.theme === 'furnitureGroup' || props.theme === 'furnitureGroupNav') ? { justifyContent: 'space-between', width: '260px' } : (props.theme === 'microphoneTypeNav') ? { width: '100%', backgroundColor: '#e0e0e0', borderRadius: '8px', padding: '3px', paddingLeft: '24px', paddingRight: '24px', display: 'flex' } : (props.theme === 'speakerCoverageNav') ? { width: '130px', backgroundColor: '#b8b8b8', borderRadius: '8px', padding: '3px', display: 'flex' } : (props.theme === 'speakerCoverageNavCustom') ? { display: 'flex', gap: '4px', background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)', borderRadius: '8px', padding: '4px', border: `1px solid ${theme.palette.divider}`, boxShadow: theme.palette.mode === 'dark' ? 'inset 0 1px 3px rgba(0, 0, 0, 0.4)' : 'inset 0 1px 2px rgba(0, 0, 0, 0.05)', height: '36px', alignItems: 'center', width: '120px' } : (props.theme === 'acousticGroupNav' || props.theme === 'microphoneCoverageNav' || props.theme === 'speakerTypeNav') ? { display: 'flex', gap: '4px', background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)', borderRadius: '8px', padding: '4px', border: `1px solid ${theme.palette.divider}`, boxShadow: theme.palette.mode === 'dark' ? 'inset 0 1px 3px rgba(0, 0, 0, 0.4)' : 'inset 0 1px 2px rgba(0, 0, 0, 0.05)', height: '36px', alignItems: 'center' } : {}}>
        {items.map((item, index) => {
          const state = getState(item);
          const isChecked = checked(item);
          
          // Note: Camera button should always be visible to allow model selection
          // Only hide if explicitly configured to not have cameras in the system
          if (item === 'camera') {
            // Keep camera section visible - users should be able to select camera models including "none"
            // if (!hasCamera && !hasVideoBar) return null;
          }
          
          // Get the icon component for navigation buttons
          const IconComponent = nav && Navigation[item as keyof typeof Navigation];
          
          // Get the icon component for furniture buttons
          const FurnitureIconComponent = (props.theme === 'furnitureGroup' || props.theme === 'furnitureGroupNav') && Furniture[item as keyof typeof Furniture];
          
          // Get the icon component for action buttons
          const ActionIconComponent = (props.theme === 'actionGroup') && Actions[item as keyof typeof Actions];
          
          // Navigation label mapping for custom display names
          const navLabelMap: Record<string, string> = {
            'furnishing': 'Furniture'
          };
          
          // For action buttons that need special styling, render them directly as Box components
          if ((props.theme === 'actionGroup') && ActionIconComponent && 
              (item === 'position' || item === 'distance' || item === 'move' || item === 'listen' || item === 'add' || item === 'remove' || item === 'delete' || item === 'measure' || item === 'block' || item === 'level' || item === 'ghostchair' || item === 'armedghostchair')) {
            return (
              <Box id={item} className={`${nav ? 'd-flex vstack gap-1' : ''}`} key={`${item}-${index}`} sx={{ marginLeft: index === 0 ? '12px' : '0' }}>
                <Tooltip
                  title={tooltips ? tooltips[item] || '' : ''}
                  open={tooltipOpen && toolTip === tooltips?.[item]}
                  onClose={handleTooltipLeave}
                  placement="top"
                >
                  <Box 
                    onMouseEnter={(e) => handleTooltipEnter(e, item)}
                    onMouseLeave={handleTooltipLeave}
                    className={`d-flex align-items-center my-${bullets ? 3 : 0}`}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        backgroundColor: 'transparent',
                        borderRadius: '4px',
                        border: isChecked ? '2px solid #000000' : '1px solid #d6d8db',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        outline: 'none',
                        userSelect: 'none',
                        pointerEvents: 'auto',
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                          borderColor: isChecked ? '#000000' : '#bdbdbd',
                        },
                        '&:active': {
                          backgroundColor: '#e9ecef',
                          borderColor: isChecked ? '#000000' : '#bdbdbd',
                        },
                        '&:focus': {
                          backgroundColor: 'transparent',
                          borderColor: isChecked ? '#000000' : '#d6d8db',
                          boxShadow: isChecked ? '0 0 0 3px rgba(0, 0, 0, 0.25)' : '0 0 0 3px rgba(108, 117, 125, 0.25)',
                        },
                        '&:focus-visible': {
                          backgroundColor: 'transparent',
                          borderColor: isChecked ? '#000000' : '#d6d8db',
                          boxShadow: isChecked ? '0 0 0 3px rgba(0, 0, 0, 0.25)' : '0 0 0 3px rgba(108, 117, 125, 0.25)',
                        }
                      }}
                      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                        const buttonEvent = e as unknown as React.MouseEvent<HTMLButtonElement>;
                        handleClick(buttonEvent, item);
                      }}
                    >
                      <SvgIcon 
                        viewBox="0 0 50 50"
                        sx={{ 
                          width: '24px',
                          height: '24px',
                          transform: 'scale(1.2)',
                          ...(item === 'level' ? {
                            '& g': {
                              stroke: isChecked ? '#fff' : '#6c757d',
                              fill: isChecked ? '#fff' : '#6c757d',
                              strokeWidth: '1',
                              strokeMiterlimit: '0'
                            }
                          } : item === 'move' ? {
                            '& g': {
                              stroke: isChecked ? '#fff' : '#6c757d',
                              strokeWidth: '2'
                            }
                          } : item === 'position' ? {
                            '& g': {
                              stroke: isChecked ? '#fff' : '#6c757d',
                              fill: isChecked ? '#fff' : '#6c757d',
                              strokeWidth: '1'
                            }
                          } : {
                            '& path, & g, & circle': {
                              stroke: isChecked ? '#fff' : '#6c757d',
                              fill: (item === 'listen' || item === 'add' || item === 'remove' || item === 'delete' || item === 'measure' || item === 'block' ? 'none' : (isChecked ? '#fff' : '#6c757d')),
                              strokeWidth: (item === 'add' || item === 'remove' || item === 'delete' || item === 'block' ? '2' : (item === 'listen' || item === 'measure' ? '1.5' : '1')),
                              opacity: '1'
                            }
                          })
                        }}
                      >
                        {item === 'level' ? (
                          <g stroke={isChecked ? '#fff' : '#6c757d'} fill={isChecked ? '#fff' : '#6c757d'} strokeWidth="1" strokeMiterlimit="0">
                            <path d="m7 32h15a3 3 0 0 1 6 0h15v2a4 4 0 0 1-4 4h-28a4 4 0 0 1-4-4v-2z"></path>
                            <path d="m7 32v-16a4 4 0 0 1 4-4h28a4 4 0 0 1 4 4v16" fill="none"></path>
                            <path d="M 30.49 18.22A 13 13 0 0 0 13.74 23.5" fill="none" strokeWidth="2"></path>
                            <path d="M 36.26 23.5A 13 13 0 0 0 32.46 19.35" fill="none" strokeWidth="2"></path>
                            <path d="m26 30l4.6-9" strokeWidth="2"></path>
                          </g>
                        ) : item === 'armedghostchair' ? (
                          <g fill={isChecked ? '#fff' : '#6c757d'} stroke="none">
                            <path d="M14 13h22c2 0 3 1 3 3v2c0 1-1 2-2 2H13c-1 0-2-1-2-2v-2c0-2 1-3 3-3z"/>
                            <path d="M10 16c-1 0-2 1-2 2v12c0 2 1 3 2 3h2c1 0 2-1 2-3V18c0-1-1-2-2-2h-2z"/>
                            <path d="M36 16c1 0 2 1 2 2v12c0 2-1 3-2 3h-2c-1 0-2-1-2-3V18c0-1 1-2 2-2h2z"/>
                            <path d="M14 18h22c1 0 2 1 2 2v12c0 2-1 3-2 3H14c-2 0-3-1-3-3V20c0-1 1-2 3-2z"/>
                          </g>
                        ) : (
                          <ActionIconComponent 
                            state={{ 
                              icon_stroke: isChecked ? '#fff' : '#6c757d',
                              icon_fill: (item === 'move' || item === 'position' ? (isChecked ? '#fff' : '#6c757d') : (item === 'listen' || item === 'add' || item === 'remove' || item === 'delete' || item === 'measure' || item === 'block' ? 'none' : (isChecked ? '#fff' : '#6c757d'))),
                              border_stroke: isChecked ? '#fff' : '#6c757d',
                              border_fill: (item === 'move' ? 'none' : (item === 'position' ? (isChecked ? '#fff' : '#6c757d') : (item === 'listen' || item === 'add' || item === 'remove' || item === 'delete' || item === 'measure' || item === 'block' ? 'none' : (isChecked ? '#fff' : '#6c757d'))))
                            }}
                          />
                        )}
                      </SvgIcon>
                    </Box>
                    {bullets && <MuiBulletList content={bullets[index]} />}
                  </Box>
                </Tooltip>
                {children && React.isValidElement(children) && 
                  React.cloneElement(children, { item } as any)}
              </Box>
            );
          }

          // Create the button content with caret, icon and text
          const buttonContent = nav && labels ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Caret arrow on the left */}
              <Box sx={{ 
                color: isChecked ? '#007bff' : '#b0b0b0',
                fontSize: '16px',
                lineHeight: 1,
                width: '12px',
                display: 'flex',
                justifyContent: 'center',
                transform: isChecked ? 'rotate(90deg)' : 'none',
                transition: 'transform 0.2s ease-in-out'
              }}>
                ›
              </Box>
              {IconComponent && (
                <SvgIcon 
                  viewBox="0 0 50 50"
                  sx={{ 
                    width: '24px', 
                    height: '24px',
                    flexShrink: 0,
                    '& path, & g, & circle': {
                      stroke: isChecked ? '#007bff' : '#b0b0b0',
                      fill: 'none',
                      strokeWidth: '1.5'
                    }
                  }}
                >
                  <IconComponent 
                    state={{ 
                      icon_stroke: isChecked ? '#007bff' : '#b0b0b0',
                      icon_fill: 'none',
                      border_stroke: isChecked ? '#007bff' : '#b0b0b0',
                      border_fill: 'none'
                    }}
                    center={false}
                  />
                </SvgIcon>
              )}
              <Box sx={{ textTransform: 'capitalize', color: isChecked ? '#007bff' : '#b0b0b0' }}>
                {navLabelMap[item] || item.replace('_', ' ')}
              </Box>
            </Box>
          ) : (props.theme === 'furnitureGroup' || props.theme === 'furnitureGroupNav') && FurnitureIconComponent ? (
            <SvgIcon 
              viewBox="0 0 50 50"
              sx={{ 
                width: '48px', 
                height: '48px',
                '& path, & g, & circle, & rect': {
                  stroke: theme.palette.text.secondary,
                  fill: isChecked ? theme.palette.text.secondary : 'transparent',
                  strokeWidth: '2'
                }
              }}
            >
              <FurnitureIconComponent 
                state={{ 
                  icon_stroke: theme.palette.text.secondary,
                  icon_fill: 'transparent',
                  border_stroke: theme.palette.text.secondary,
                  border_fill: isChecked ? theme.palette.text.secondary : 'transparent'
                }}
                className=""
                id=""
              />
            </SvgIcon>
          ) : (props.theme === 'actionGroup') && ActionIconComponent ? (
            <SvgIcon 
              viewBox="0 0 50 50"
              sx={{ 
                width: '24px',
                height: '24px',
                '& path, & g, & circle': {
                  stroke: isChecked ? '#dc3545' : '#6c757d',
                  fill: isChecked ? '#dc3545' : 'none',
                  strokeWidth: '1.5',
                  opacity: '1'
                }
              }}
            >
              <ActionIconComponent 
                state={{ 
                  icon_stroke: isChecked ? '#dc3545' : '#6c757d',
                  icon_fill: isChecked ? '#dc3545' : 'none',
                  border_stroke: isChecked ? '#dc3545' : '#6c757d',
                  border_fill: isChecked ? '#dc3545' : 'none'
                }}
              />
            </SvgIcon>
          ) : (labels ? item : props.children ? null : item);
          
          // Create properly typed button props
          const buttonProps: {
            variant: 'text' | 'contained' | 'outlined';
            size: 'small' | 'medium' | 'large';
            onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
            children: React.ReactNode;
            className?: string;
            isSelected?: boolean;
            disableRipple?: boolean;
            sx?: any;
          } = {
            variant: 'text',
            size: 'medium',
            onClick: (e: React.MouseEvent<HTMLButtonElement>) => handleClick(e, item),
            children: buttonContent,
            className: nav ? `${buttonClass} ${isChecked ? 'nav-selected' : ''}` : buttonClass,
            disableRipple: true,
            sx: {
              transition: 'none !important',
              animation: 'none !important',
              backgroundColor: 'transparent !important',
              color: 'inherit !important',
              '&:hover': {
                transition: 'none !important',
                animation: 'none !important',
                backgroundColor: 'transparent !important',
                color: 'inherit !important',
                boxShadow: 'none !important',
                transform: 'none !important',
                opacity: '1 !important'
              },
              '&:active': {
                transition: 'none !important',
                animation: 'none !important',
                backgroundColor: 'transparent !important',
                color: 'inherit !important',
                boxShadow: 'none !important',
                transform: 'none !important',
                opacity: '1 !important'
              },
              '&:focus': {
                transition: 'none !important',
                animation: 'none !important',
                backgroundColor: 'transparent !important',
                color: 'inherit !important',
                boxShadow: 'none !important',
                outline: 'none !important',
                transform: 'none !important',
                opacity: '1 !important'
              },
              '&:focus-visible': {
                transition: 'none !important',
                animation: 'none !important',
                backgroundColor: 'transparent !important',
                color: 'inherit !important',
                boxShadow: 'none !important',
                outline: 'none !important',
                transform: 'none !important',
                opacity: '1 !important'
              },
              '& .MuiTouchRipple-root': {
                display: 'none !important'
              },
              '& .MuiTouchRipple-child': {
                display: 'none !important'
              },
              '&.MuiButton-root': {
                backgroundColor: 'transparent !important',
                '&:hover': {
                  backgroundColor: 'transparent !important',
                  boxShadow: 'none !important'
                },
                '&:active': {
                  backgroundColor: 'transparent !important',
                  boxShadow: 'none !important'
                },
                '&:focus': {
                  backgroundColor: 'transparent !important',
                  boxShadow: 'none !important',
                  outline: 'none !important'
                }
              }
            },
            ...(nav && { isSelected: isChecked })
          };
          
          // Create the button with proper styling for different navigation themes
          const isAcousticNavTheme = typeof props.theme === 'string' && props.theme === 'acousticGroupNav';
          const isMicrophoneTypeTheme = typeof props.theme === 'string' && props.theme === 'microphoneTypeNav';
          const isMicrophoneCoverageTheme = typeof props.theme === 'string' && props.theme === 'microphoneCoverageNav';
          const isSpeakerTypeTheme = typeof props.theme === 'string' && props.theme === 'speakerTypeNav';
          const isSpeakerCoverageTheme = typeof props.theme === 'string' && props.theme === 'speakerCoverageNav';
          const isSpeakerCoverageCustomTheme = typeof props.theme === 'string' && props.theme === 'speakerCoverageNavCustom';
          const isFurnitureTheme = typeof props.theme === 'string' && (props.theme === 'furnitureGroup' || props.theme === 'furnitureGroupNav');
          
          
          
          if (isAcousticNavTheme) {
            // Use UnitSwitch pattern for acoustics nav
            return (
              <Box
                key={`${item}-${index}`}
                sx={{
                  height: '28px',
                  padding: '0 12px',
                  borderRadius: '4px',
                  border: 'none',
                  background: isChecked
                    ? theme.palette.background.default
                    : 'transparent',
                  overflow: 'hidden',
                  color: isChecked ? theme.palette.text.primary : theme.palette.text.secondary,
                  textOverflow: 'ellipsis',
                  fontFamily: '"Open Sans"',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: isChecked ? 600 : 500,
                  lineHeight: '150%',
                  letterSpacing: '-0.28px',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  textTransform: 'capitalize',
                  minWidth: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  boxShadow: isChecked
                    ? theme.palette.mode === 'dark'
                      ? '0px 1px 3px rgba(0, 0, 0, 0.3)'
                      : '0px 1px 2px rgba(0, 0, 0, 0.1)'
                    : 'none',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: isChecked
                      ? theme.palette.background.default
                      : theme.palette.action.hover,
                    color: isChecked ? theme.palette.text.primary : theme.palette.text.primary,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: '2px',
                  },
                }}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => handleClick(e as any, item)}
              >
                {item}
              </Box>
            );
          }
          
          if (isMicrophoneTypeTheme) {
            // For microphone type nav, use same styling as acoustics
            const displayText = bubbles?.[item] || item;
            
            return (
              <Box 
                key={`${item}-${index}`}
                sx={{
                  display: 'inline-flex',
                  flex: 1,
                  minWidth: '40px',
                  height: '22px',
                  backgroundColor: isChecked ? '#ffffff' : 'transparent',
                  color: isChecked ? '#000000' : '#666666',
                  border: 'none',
                  borderRadius: isChecked ? '5px' : '0',
                  fontSize: '11px',
                  fontWeight: '400',
                  textTransform: 'capitalize',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  padding: '0 4px',
                  margin: isChecked ? '1px' : '0',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: isChecked ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  '&:hover': {
                    backgroundColor: isChecked ? '#ffffff' : 'rgba(255,255,255,0.1)',
                  },
                }}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => handleClick(e as any, item)}
              >
                {displayText}
              </Box>
            );
          }
          
          if (isMicrophoneCoverageTheme) {
            // For microphone coverage nav themes, use UnitSwitch pattern
            const displayText = bubbles?.[item] || item;

            return (
              <Box
                key={`${item}-${index}`}
                sx={{
                  height: '28px',
                  padding: '0 12px',
                  borderRadius: theme.shape.borderRadius,
                  border: 'none',
                  background: isChecked
                    ? theme.palette.background.default
                    : 'transparent',
                  overflow: 'hidden',
                  color: isChecked ? theme.palette.text.primary : theme.palette.text.secondary,
                  textOverflow: 'ellipsis',
                  fontFamily: '"Open Sans"',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: isChecked ? 600 : 500,
                  lineHeight: '150%',
                  letterSpacing: '-0.28px',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  textTransform: 'none',
                  minWidth: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  boxShadow: isChecked
                    ? theme.palette.mode === 'dark'
                      ? '0px 1px 3px rgba(0, 0, 0, 0.3)'
                      : '0px 1px 2px rgba(0, 0, 0, 0.1)'
                    : 'none',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: isChecked
                      ? theme.palette.background.default
                      : theme.palette.action.hover,
                    color: isChecked ? theme.palette.text.primary : theme.palette.text.primary,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: '2px',
                  },
                }}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => handleClick(e as any, item)}
              >
                {displayText}
              </Box>
            );
          }
          
          if (isSpeakerCoverageTheme) {
            // For speaker coverage nav, use exact same styling as acoustics section
            return (
              <Box 
                key={`${item}-${index}`}
                sx={{
                  display: 'inline-flex',
                  flex: 1,
                  minWidth: '40px',
                  height: '22px',
                  backgroundColor: isChecked ? '#ffffff' : 'transparent',
                  color: isChecked ? '#000000' : '#666666',
                  border: 'none',
                  borderRadius: isChecked ? '5px' : '0',
                  fontSize: '11px',
                  fontWeight: '400',
                  textTransform: 'capitalize',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  padding: '0 4px',
                  margin: isChecked ? '1px' : '0',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: isChecked ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                  whiteSpace: 'nowrap',
                }}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => handleClick(e as any, item)}
              >
                {bubbles?.[item] || item}
              </Box>
            );
          }
          
          if (isSpeakerCoverageCustomTheme) {
            // Use UnitSwitch pattern for speaker coverage custom
            return (
              <Box
                key={`${item}-${index}`}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => handleClick(e as any, item)}
                sx={{
                  height: '28px',
                  padding: '0 12px',
                  borderRadius: '4px',
                  border: 'none',
                  background: isChecked
                    ? theme.palette.background.default
                    : 'transparent',
                  overflow: 'hidden',
                  color: isChecked ? theme.palette.text.primary : theme.palette.text.secondary,
                  textOverflow: 'ellipsis',
                  fontFamily: '"Open Sans"',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: isChecked ? 600 : 500,
                  lineHeight: '150%',
                  letterSpacing: '-0.28px',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  textTransform: 'none',
                  minWidth: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  boxShadow: isChecked
                    ? theme.palette.mode === 'dark'
                      ? '0px 1px 3px rgba(0, 0, 0, 0.3)'
                      : '0px 1px 2px rgba(0, 0, 0, 0.1)'
                    : 'none',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: isChecked
                      ? theme.palette.background.default
                      : theme.palette.action.hover,
                    color: isChecked ? theme.palette.text.primary : theme.palette.text.primary,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                {bubbles?.[item] || item}
              </Box>
            );
          }
          
          if (isSpeakerTypeTheme) {
            // For speaker type nav themes, use UnitSwitch pattern
            const displayText = bubbles?.[item] || item;

            return (
              <Box
                key={`${item}-${index}`}
                sx={{
                  height: '28px',
                  padding: '0 12px',
                  borderRadius: theme.shape.borderRadius,
                  border: 'none',
                  background: isChecked
                    ? theme.palette.background.default
                    : 'transparent',
                  overflow: 'hidden',
                  color: isChecked ? theme.palette.text.primary : theme.palette.text.secondary,
                  textOverflow: 'ellipsis',
                  fontFamily: '"Open Sans"',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: isChecked ? 600 : 500,
                  lineHeight: '150%',
                  letterSpacing: '-0.28px',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  textTransform: 'none',
                  minWidth: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  boxShadow: isChecked
                    ? theme.palette.mode === 'dark'
                      ? '0px 1px 3px rgba(0, 0, 0, 0.3)'
                      : '0px 1px 2px rgba(0, 0, 0, 0.1)'
                    : 'none',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: isChecked
                      ? theme.palette.background.default
                      : theme.palette.action.hover,
                    color: isChecked ? theme.palette.text.primary : theme.palette.text.primary,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: '2px',
                  },
                }}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => handleClick(e as any, item)}
              >
                {displayText}
              </Box>
            );
          }
          
          if (isFurnitureTheme && FurnitureIconComponent) {
            // For furniture theme, render SVG directly without any Box wrapper
            return (
              <SvgIcon 
                key={`${item}-${index}`}
                viewBox="4 12 42 30"
                sx={{ 
                  width: '60px', 
                  height: '34px',
                  cursor: 'pointer',
                  margin: '0',
                  padding: '0',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    opacity: 0.8,
                  },
                  '& path, & g, & circle, & rect': {
                    stroke: theme.palette.text.secondary,
                    fill: isChecked ? theme.palette.text.secondary : 'transparent',
                    strokeWidth: '1'
                  }
                }}
                onClick={(e: React.MouseEvent<SVGSVGElement>) => handleClick(e as any, item)}
              >
                <FurnitureIconComponent 
                  state={{ 
                    icon_stroke: theme.palette.text.secondary,
                    icon_fill: 'transparent',
                    border_stroke: theme.palette.text.secondary,
                    border_fill: isChecked ? theme.palette.text.secondary : 'transparent'
                  }}
                  className=""
                  id=""
                />
              </SvgIcon>
            );
          }

          const AcousticNavButton = styled(Button, {
            shouldForwardProp: (prop) => prop !== 'isSelected',
          })<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
            // Regular button styling for non-acoustic themes
          }));

          return (
            <Box id={item} className={`${nav ? 'd-flex vstack gap-1' : ''}`} key={`${item}-${index}`}>
              <Tooltip
                title={tooltips ? tooltips[item] || '' : ''}
                open={tooltipOpen && toolTip === tooltips?.[item]}
                onClose={handleTooltipLeave}
                placement="top"
              >
                <Box 
                  onMouseEnter={(e) => handleTooltipEnter(e, item)}
                  onMouseLeave={handleTooltipLeave}
                  className={`d-flex align-items-center my-${bullets ? 3 : 0}`}
                >
                  {nav ? (
                    <StyledNavButton {...buttonProps} className={buttonClass} />
                  ) : isAcousticNavTheme ? (
                    <AcousticNavButton {...buttonProps} isSelected={isChecked} />
                  ) : (
                    <Button {...buttonProps} className={buttonClass} />
                  )}
                  {bullets && <MuiBulletList content={bullets[index]} />}
                </Box>
              </Tooltip>
              {children && React.isValidElement(children) && 
                React.cloneElement(children, { item } as any)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * Helper function to safely call getCurrentValue2 with TypeScript
 * @param state - The Redux state
 * @param param - The parameter to get value for
 * @param name - Optional name parameter
 * @returns The value from state
 */
const safeGetValue = (state: any, param: string, name?: string) => {
  // Cast the function to allow any number of arguments
  const getValue = getCurrentValue2 as any;
  return name ? getValue(state, param, name) : getValue(state, param);
};

/**
 * MUI SelectButtons component
 * 
 * @param props - Component props
 * @returns MUI SelectButtons component
 */
export const MuiSelectButtons: React.FC<SelectButtonsProps> = (props) => {
  const dispatch = useDispatch();
  const currentValue = useSelector((state: any) => 
    props.dispatcher ? safeGetValue(state, props.dispatcher) : null
  );
  const [buttonActive, setButtonActive] = useState<Record<string, boolean>>({});
  
  useEffect(() => {
    if (!currentValue) return;
    
    const newValue = props.items?.reduce((acc, curr) => { 
      const key = findKey(currentValue, (_, key) => curr.includes(key));
      return { ...acc, [curr]: key ? Boolean(currentValue[key]) : false };
    }, {} as Record<string, boolean>) || {};
    
    setButtonActive(newValue);
  }, [currentValue, props.items]);
  
  const handleClick = (evt: React.MouseEvent, item: string): void => {
    setButtonActive({ ...buttonActive, [item]: !buttonActive[item] });
    if (props.dispatcher) {
      dispatch(ReducersIndex.container[props.dispatcher](item) as unknown as AnyAction);
    }
  };
  
  const getState = (item: string): string => 
    buttonActive[item] ? props.activeState || "button_selected" : props.inactiveState || "button_active";
    
  const checked = (item: string): boolean => Boolean(buttonActive[item]);
  
  return (
    <ButtonGroup
      {...props}
      handleClick={handleClick}
      getState={getState}
      checked={checked}
    />
  );
};

/**
 * MUI ToggleButtons component
 * 
 * @param props - Component props
 * @returns MUI ToggleButtons component
 */
export const MuiToggleButtons: React.FC<ToggleButtonsProps> = React.memo((props) => {
  const dispatch = useDispatch();
  const currentValue = useSelector((state: any) => {
    if (props.dispatcher && props.name) {
      // For container reducers, the state is nested under areas[activeTab].
      // activeTab defaults to "" for single-area tools (e.g. Conference Room Designer),
      // and areas[""] is the valid area key in that case, so this must check that the
      // area itself resolves rather than requiring activeTab to be truthy — an empty
      // string activeTab was previously treated as "no active tab" and fell through to
      // the legacy fallback below, which does not resolve nested properties correctly.
      const activeTab = state.container?.activeTab;
      const area = state.container?.areas?.[activeTab];
      if (area) {
        // Map dispatcher action names to their corresponding area properties
        const dispatcherToPropertyMap: Record<string, string> = {
          'setMicrophones': 'microphones',
          'setSpeakers': 'speakers',
          'setCameras': 'cameras',
          'setAcoustics': 'acoustics',
          'setTable': 'table',
          'setDoor': 'door',
          'setCeilingTiles': 'ceilingTiles'
        };

        const property = dispatcherToPropertyMap[props.dispatcher];
        if (property && area[property]) {
          // Map name to the actual property in the device object
          const nameToPropertyMap: Record<string, string> = {
            'type': 'model',  // microphone/speaker/camera type is stored as 'model'
            'coverage': 'coverage',
            'model': 'model'
          };

          const actualProperty = nameToPropertyMap[props.name] || props.name;
          return area[property][actualProperty];
        }
      }

      // Fallback to original logic for non-container reducers
      const statePath = props.dispatcher;
      return safeGetValue(state, statePath, props.name);
    } else if (props.dispatcher) {
      return safeGetValue(state, props.dispatcher);
    }
    return null;
  });

  // Helper function to safely call getConditionResult with TypeScript
  const safeGetCondition = (state: any, condition: any) => {
    // Cast the function to allow any arguments
    const getCondition = getConditionResult as any;
    return getCondition(state, condition);
  };

  // Memoize the expensive keyCondition calculation to prevent lag
  const currentUse = useSelector((state: any) => {
    if (!props.keyCondition) return null;
    return safeGetCondition(state, props.keyCondition);
  }, (prev, next) => {
    // Only re-compute if the result actually changed
    return prev === next;
  });
  
  const [buttonActive, setButtonActive] = useState<string | null>();
  
  // Memoize available items calculation for better performance
  const availableItems = useMemo(() => {
    if (!props.items) return [];
    
    if (isArray(props.items)) {
      return props.items;
    } else {
      const itemKey = currentUse ?? keys(props.items)[0];
      return (props.items as Record<string, string[]>)[itemKey] || [];
    }
  }, [props.items, currentUse]);
  
  useEffect(() => {
    setButtonActive(currentValue);
  }, [currentValue]);
  
  const handleClick = (evt: React.MouseEvent, item: string): void => {
    const dispatcher = props.dispatcher 
      ? ReducersIndex[props.nav ? 'uiReducers' : 'container'][props.dispatcher] 
      : null;
      
    const newItem = props.deselect && item === buttonActive ? null : item.replace('_', '');
    const newValue = props.name ? { ...{}, [props.name]: newItem } : newItem;
    
    if (dispatcher) {
      dispatch(dispatcher(newValue) as unknown as AnyAction);
    }
    
    setButtonActive(newItem);
  };
  
  const getState = (item: string): string => {
    const normalizedItem = item.replace('_', '');
    return buttonActive === normalizedItem 
      ? props.activeState || "button_selected" 
      : props.inactiveState || "button_active";
  };
  
  const checked = (item: string): boolean => {
    const normalizedItem = item.replace('_', '');
    return buttonActive === normalizedItem;
  };
  
  return (
    <ButtonGroup
      {...props}
      handleClick={handleClick}
      getState={getState}
      checked={checked}
      items={availableItems}
    />
  );
});

/**
 * MUI ToggleSelect component
 * 
 * @param props - Component props
 * @returns MUI ToggleSelect component
 */
export const MuiToggleSelect: React.FC<ToggleSelectProps> = React.memo((props) => {
  const dispatch = useDispatch();
  
  // Optimize currentValue selector with memoization and shallow equality
  const currentValue = useSelector((state: any) => {
    if (props.dispatcher && props.name) {
      // For container reducers, the state is nested under areas[activeTab].
      // activeTab defaults to "" for single-area tools (e.g. Conference Room Designer),
      // and areas[""] is the valid area key in that case, so this must check that the
      // area itself resolves rather than requiring activeTab to be truthy — an empty
      // string activeTab was previously treated as "no active tab" and fell through to
      // the legacy fallback below, which does not resolve nested properties like
      // microphones.coverage correctly (it returned the whole microphones object).
      const activeTab = state.container?.activeTab;
      const area = state.container?.areas?.[activeTab];
      if (area) {
        // Map dispatcher action names to their corresponding area properties
        const dispatcherToPropertyMap: Record<string, string> = {
          'setMicrophones': 'microphones',
          'setSpeakers': 'speakers',
          'setCameras': 'cameras',
          'setAcoustics': 'acoustics',
          'setTable': 'table',
          'setDoor': 'door',
          'setCeilingTiles': 'ceilingTiles'
        };

        const property = dispatcherToPropertyMap[props.dispatcher];
        if (property && area[property]) {
          // Map name to the actual property in the device object
          const nameToPropertyMap: Record<string, string> = {
            'type': 'model',  // microphone/speaker/camera type is stored as 'model'
            'coverage': 'coverage',
            'model': 'model'
          };

          const actualProperty = nameToPropertyMap[props.name] || props.name;
          return area[property][actualProperty];
        }
      }

      // Fallback to original logic for non-container reducers
      const statePath = props.dispatcher;
      return safeGetValue(state, statePath, props.name);
    } else if (props.dispatcher) {
      return safeGetValue(state, props.dispatcher);
    }
    return null;
  }, (prev, next) => {
    // Use shallow equality to prevent unnecessary re-renders
    if (prev === next) return true;
    if (!prev || !next) return false;
    if (typeof prev !== 'object' || typeof next !== 'object') return prev === next;
    
    // For objects, do a shallow comparison
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (prevKeys.length !== nextKeys.length) return false;
    
    return prevKeys.every(key => prev[key] === next[key]);
  });
  
  // Helper function to safely call getConditionResult with TypeScript
  const safeGetCondition = (state: any, condition: any) => {
    // Cast the function to allow any arguments
    const getCondition = getConditionResult as any;
    return getCondition(state, condition);
  };
  
  const currentUse = useSelector((state: any) =>
    safeGetCondition(state, {...props.keyCondition ?? {check: () => null }})
  );

  // Memoize availableItems to prevent unnecessary re-renders
  const availableItems = useMemo(() => {
    if (!props.items) return [];

    if (isArray(props.items)) {
      return props.items;
    } else {
      const itemKey = currentUse ?? keys(props.items)[0];
      return (props.items as Record<string, string[]>)[itemKey] || [];
    }
  }, [props.items, currentUse]);

  // Lazily initialize from the current Redux value instead of starting empty and waiting for
  // the sync effect below to catch up. Starting empty meant the FIRST paint always showed every
  // item as inactive — normally corrected within a tick once the effect ran, but the effect only
  // fires again on an actual dependency change. A pure view switch used to always trigger a
  // recalculation dispatch that incidentally forced an extra render (and this component to
  // remount fresh, then re-sync), papering over the empty-first-paint gap; now that a pure view
  // switch is correctly a no-op when nothing changed, there's no more incidental nudge, so this
  // control could sit on its default (nothing highlighted) until the user clicked it directly.
  const [buttonActive, setButtonActive] = useState<Record<string, boolean>>(() => {
    if (currentValue && availableItems.length > 0) {
      return availableItems.reduce((acc, curr) => ({ ...acc, [curr]: Boolean(currentValue[curr]) }), {} as Record<string, boolean>);
    }
    return {};
  });

  // Use multiple refs to track state and prevent loops
  const isUpdatingRef = useRef(false);
  const previousCurrentValueRef = useRef<any>(undefined);
  const previousAvailableItemsRef = useRef<string[]>([]);
  const stablePropsRef = useRef(props.noSelect);
  
  useEffect(() => {
    // Prevent infinite loops by checking if we're already updating
    if (isUpdatingRef.current) return;
    
    // Check if dependencies have actually changed
    const hasCurrentValueChanged = !isEqual(currentValue, previousCurrentValueRef.current);
    const hasAvailableItemsChanged = !isEqual(availableItems, previousAvailableItemsRef.current);
    const hasNoSelectChanged = props.noSelect !== stablePropsRef.current;
    
    // Only proceed if something actually changed
    if (!hasCurrentValueChanged && !hasAvailableItemsChanged && !hasNoSelectChanged) {
      return;
    }
    
    // Create expected state based on current value
    const expectedState = currentValue && availableItems.length > 0
      ? availableItems.reduce((acc, curr) => { 
          return { ...acc, [curr]: Boolean(currentValue[curr]) };
        }, {} as Record<string, boolean>) 
      : {};
    
    // Only apply noSelect logic if we have a valid currentValue object structure
    // and all values are explicitly false (not just undefined/missing)
    const hasValidStructure = currentValue && typeof currentValue === 'object';
    const allExplicitlyFalse = hasValidStructure && availableItems.every(x => currentValue[x] === false);
    
    if (allExplicitlyFalse && props.noSelect) {
      expectedState[props.noSelect] = true;
    }
    
    // Only update if the state actually needs to change
    if (!isEqual(expectedState, buttonActive)) {
      isUpdatingRef.current = true;
      setButtonActive(expectedState);
      
      // Update references after setting new value
      previousCurrentValueRef.current = currentValue;
      previousAvailableItemsRef.current = availableItems;
      stablePropsRef.current = props.noSelect;
      
      // Reset the updating flag after the state update
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    } else {
      // Update references even if buttonActive doesn't change
      previousCurrentValueRef.current = currentValue;
      previousAvailableItemsRef.current = availableItems;
      stablePropsRef.current = props.noSelect;
    }
  }, [currentValue, availableItems, props.noSelect]); // Removed buttonActive from dependencies to prevent loops
  
  const defaultState = availableItems?.reduce(
    (acc, curr) => ({ ...acc, [curr]: false }), 
    {} as Record<string, boolean>
  );
  
  const toggleItems = props.toggle ?? props.items ?? [];
  const toggles = (Array.isArray(toggleItems) ? toggleItems : []).reduce(
    (acc, curr) => ({ ...acc, [curr]: false }), 
    {} as Record<string, boolean>
  );
  
  // Conditional to disable buttons while keeping the last value - used for coverage undo
  const override = useSelector((state: any) => 
    safeGetCondition(state, {...props.activeCondition ?? {check: () => false }})
  );
  
  const handleClick = (evt: React.MouseEvent, item: string): void => {
    // Prevent multiple rapid clicks that can cause loops
    if (isUpdatingRef.current) return;
    
    isUpdatingRef.current = true;
    
    const isToggle = (props.toggle ?? props.items)?.includes(item);
    const newItem = isToggle 
      ? { ...defaultState, [item]: true } 
      : { ...buttonActive, ...toggles, [item]: !buttonActive[item] };
      
    if (availableItems.every(x => !newItem[x]) && props.noSelect) {
      newItem[props.noSelect] = true;
    }
    
    setButtonActive(newItem);
    
    const newValue = props.name ? { ...{}, [props.name]: newItem } : newItem;
    if (props.dispatcher) {
      dispatch(ReducersIndex.container[props.dispatcher](newValue) as unknown as AnyAction);
    }
    
    // Reset the updating flag after a short delay
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 10);
  };
  
  const getState = (item: string): string => 
    override 
      ? 'button_disabled' 
      : buttonActive[item] 
        ? props.activeState || "button_selected" 
        : props.inactiveState || "button_disabled";
        
  const checked = (item: string): boolean => Boolean(buttonActive[item]);
  
  return (
    <ButtonGroup
      {...props}
      handleClick={handleClick}
      getState={getState}
      checked={checked}
      items={availableItems}
    />
  );
});

/**
 * MUI DotButtons component
 * 
 * @param props - Component props
 * @returns MUI DotButtons component
 */
export const MuiDotButtons: React.FC<DotButtonsProps> = (props) => {
  const currentValue = props.current;
  const [buttonActive, setButtonActive] = useState<string>(currentValue);
  
  useEffect(() => {
    setButtonActive(currentValue);
  }, [currentValue]);
  
  const handleClick = (evt: React.MouseEvent, item: string): void => setButtonActive(item);
  const getState = (item: string): string => buttonActive === item ? "dot_selected" : "dot_active";
  const checked = (item: string): boolean => buttonActive === item;
  
  return (
    <ButtonGroup
      {...props}
      className='justify-content-center mt-4'
      gap={2}
      handleClick={handleClick}
      getState={getState}
      checked={checked}
    />
  );
};

/**
 * MUI NavButtons component
 * 
 * @param props - Component props
 * @returns MUI NavButtons component
 */
export const MuiNavButtons: React.FC<NavButtonsProps> = (props) => <MuiToggleButtons {...props} nav />;

/**
 * MUI FamilyButtons component
 * 
 * @param props - Component props
 * @returns MUI FamilyButtons component
 */
export const MuiFamilyButtons: React.FC<FamilyButtonsProps> = (props) => {
  const newProps = {
    ...props,
    labels: true,
    inactiveState: 'acoustic_inactive',
    items: keys(props.items),
    bullets: values(props.items),
  } as ToggleButtonsProps;
  
  return <MuiToggleButtons {...newProps} />;
};

export type {
  ButtonGroupProps,
  SelectButtonsProps,
  ToggleButtonsProps,
  ToggleSelectProps,
  DotButtonsProps,
  NavButtonsProps,
  FamilyButtonsProps
};
export default ButtonGroup;
